import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { commonStyles } from "@/lib/theme";
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameMonth, 
  isToday,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  isSameDay,
  getWeek,
  addDays,
  subDays
} from "date-fns";
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight, 
  LayoutDashboard,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { DayDetail } from "@/components/DayDetail";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface FocusModeProps {
  isDarkMode?: boolean;
  onBackToDashboard?: () => void;
  demoMode?: boolean;
}

interface DayCardProps {
  date: Date;
  isCurrentMonth: boolean;
  isSelected: boolean;
  onClick: () => void;
  isDarkMode: boolean;
  isMobile?: boolean;
}

const DayCard: React.FC<DayCardProps> = ({ date, isCurrentMonth, isSelected, onClick, isDarkMode, isMobile = false }) => {
  const isCurrentDay = isToday(date);
  
  // Simplified mobile view
  if (isMobile) {
    return (
      <div 
        className={cn(
          "cursor-pointer transition-colors h-16 w-full flex flex-col justify-center items-center px-2 py-1 rounded-md border",
          !isCurrentMonth && "opacity-40",
          isCurrentDay && "border-[#CCF751] shadow-[#CCF751]/20 shadow-lg",
          isSelected && "ring-2 ring-[#CCF751] bg-[#818881]/40",
          isDarkMode ? "border-[#818881]" : "border-gray-200"
        )}
        onClick={onClick}
      >
        <span className={cn(
          "text-xs",
          isDarkMode ? "text-[#818881]" : "text-gray-500"
        )}>{format(date, 'E')}</span>
        <span className={cn(
          "text-xl font-bold",
          isDarkMode ? "text-[#D9D9D9]" : "text-gray-900"
        )}>{format(date, 'd')}</span>
      </div>
    );
  }
  
  // Desktop view (unchanged)
  return (
    <Card 
      className={cn(
        commonStyles.card,
        "cursor-pointer transition-all hover:scale-105 hover:shadow-lg",
        !isCurrentMonth && "opacity-40",
        isCurrentDay && "border-[#CCF751] shadow-[#CCF751]/20 shadow-lg",
        isSelected && "ring-2 ring-[#CCF751] ring-offset-2 ring-offset-[#2C2E2D]"
      )}
      onClick={onClick}
    >
      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <div>
            <div className="flex items-baseline gap-2">
            <p className={cn(
              "text-sm",
              isDarkMode ? "text-[#818881]" : "text-gray-500"
            )}>{format(date, 'EEEE')}</p>
              <span className={cn(
                "text-xs uppercase",
                isDarkMode ? "text-[#818881]" : "text-gray-500"
              )}>{format(date, 'MMM')}</span>
            </div>
            <h3 className={cn(
              "text-2xl font-bold",
              isDarkMode ? "text-[#D9D9D9]" : "text-gray-900"
            )}>{format(date, 'd')}</h3>
          </div>
          {isCurrentDay && (
            <span className="px-2 py-1 text-xs rounded-full bg-[#CCF751] text-[#2C2E2D]">
              Today
            </span>
          )}
        </div>
        
        <div className="mt-4 space-y-2">
          <div className={cn(
            "flex items-center text-sm",
            isDarkMode ? "text-[#818881]" : "text-gray-500"
          )}>
            <CalendarIcon className="w-4 h-4 mr-2" />
            <span>View tasks</span>
          </div>
        </div>
      </div>
    </Card>
  );
};

type ViewMode = 'month' | 'week' | 'day';

const FocusMode: React.FC<FocusModeProps> = ({ isDarkMode = true, onBackToDashboard, demoMode = false }) => {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const [showDayDetail, setShowDayDetail] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [calendarExpanded, setCalendarExpanded] = useState<boolean>(false);

  useEffect(() => {
    // Check if screen is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setSelectedDate(new Date());
    setShowCalendar(false);
    setShowDayDetail(true);
  }, []);

  const getDaysToShow = () => {
    if (viewMode === 'month') {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const calendarStart = startOfWeek(monthStart);
      const calendarEnd = endOfWeek(monthEnd);
      return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    } else if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      return eachDayOfInterval({ start: weekStart, end: weekEnd });
    } else {
      // Day view just shows a few days around the current day
      const dayStart = subDays(currentDate, 1);
      const dayEnd = addDays(currentDate, 5);
      return eachDayOfInterval({ start: dayStart, end: dayEnd });
    }
  };

  const handleDayClick = (date: Date) => {
    setSelectedDate(date);
    setShowDayDetail(true);
    setShowCalendar(false);
  };

  const navigate = (direction: 'prev' | 'next', jump: 'normal' | 'big') => {
    if (viewMode === 'month') {
      if (jump === 'normal') {
        setCurrentDate(prev => direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1));
      } else {
        setCurrentDate(prev => direction === 'prev' ? subMonths(prev, 3) : addMonths(prev, 3));
      }
    } else if (viewMode === 'week') {
      if (jump === 'normal') {
        setCurrentDate(prev => direction === 'prev' ? subWeeks(prev, 1) : addWeeks(prev, 1));
      } else {
        setCurrentDate(prev => direction === 'prev' ? subWeeks(prev, 4) : addWeeks(prev, 4));
      }
    } else {
      // Day view
      if (jump === 'normal') {
        setCurrentDate(prev => direction === 'prev' ? subDays(prev, 1) : addDays(prev, 1));
      } else {
        setCurrentDate(prev => direction === 'prev' ? subDays(prev, 7) : addDays(prev, 7));
      }
    }
  };

  const toggleView = () => {
    setShowCalendar(!showCalendar);
    if (showCalendar) {
      setShowDayDetail(true);
    }
  };

  const toggleCalendarExpand = () => {
    setCalendarExpanded(!calendarExpanded);
  };

  const calendarDays = getDaysToShow();

  return (
    <div className="space-y-4 w-full">
      {/* Header with date info */}
      <div className="flex flex-col md:flex-row md:items-center justify-between bg-[#2C2E2D] p-4 rounded-lg border border-[#818881]">
        <div>
          <h1 className="text-2xl font-semibold text-[#D9D9D9]">
            {format(selectedDate || new Date(), 'EEEE, MMMM d')}
          </h1>
          <p className="text-[#818881]">
            {showCalendar 
              ? `Week ${getWeek(currentDate)} - ${format(currentDate, 'MMMM yyyy')}`
              : "Plan your job applications and daily tasks"
            }
          </p>
        </div>
        
        {/* Desktop controls */}
        <div className="hidden md:flex gap-2 mt-4 md:mt-0">
          {showCalendar && (
            <>
              <div className="flex items-center gap-2 mr-4">
                <Select value={viewMode} onValueChange={(value: ViewMode) => setViewMode(value)}>
                  <SelectTrigger className={cn(
                    "w-[120px]",
                    isDarkMode 
                      ? "bg-[#2C2E2D] border-[#818881] text-[#D9D9D9]"
                      : "bg-white"
                  )}>
                    <SelectValue placeholder="View" />
                  </SelectTrigger>
                  <SelectContent className={cn(
                    isDarkMode ? "bg-[#2C2E2D] border-[#818881]" : "bg-white"
                  )}>
                    <SelectItem value="month">Month</SelectItem>
                    <SelectItem value="week">Week</SelectItem>
                    <SelectItem value="day">Day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate('prev', 'big')}
                className={cn(
                  isDarkMode 
                    ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]"
                    : "text-gray-600 hover:text-gray-900"
                )}
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate('prev', 'normal')}
                className={cn(
                  isDarkMode 
                    ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]"
                    : "text-gray-600 hover:text-gray-900"
                )}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => setCurrentDate(new Date())}
                className={cn(
                  "px-4 min-w-[100px]",
                  isDarkMode 
                    ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]"
                    : "text-gray-600 hover:text-gray-900"
                )}
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate('next', 'normal')}
                className={cn(
                  isDarkMode 
                    ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]"
                    : "text-gray-600 hover:text-gray-900"
                )}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate('next', 'big')}
                className={cn(
                  isDarkMode 
                    ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]"
                    : "text-gray-600 hover:text-gray-900"
                )}
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </>
          )}
          <Button
            variant="outline"
            size="icon"
            onClick={toggleView}
            className={cn(
              isDarkMode 
                ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]"
                : "text-gray-600 hover:text-gray-900",
              showCalendar && "bg-[#CCF751] text-[#2C2E2D] hover:bg-[#CCF751]/90 border-[#CCF751]"
            )}
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
          {onBackToDashboard && (
            <Button
              variant="outline"
              onClick={onBackToDashboard}
              className="bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#CCF751]/90 hover:text-[#2C2E2D] hover:border-[#CCF751]"
            >
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          )}
        </div>
        
        {/* Mobile controls */}
        <div className="flex md:hidden gap-2 mt-4 justify-between">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate('prev', 'normal')}
            className={cn(
              isDarkMode 
                ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]"
                : "text-gray-600 hover:text-gray-900"
            )}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <Button
            variant="outline"
            onClick={() => setCurrentDate(new Date())}
            className={cn(
              "px-2",
              isDarkMode 
                ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]"
                : "text-gray-600 hover:text-gray-900"
            )}
          >
            Today
          </Button>
          
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate('next', 'normal')}
            className={cn(
              isDarkMode 
                ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]"
                : "text-gray-600 hover:text-gray-900"
            )}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={toggleCalendarExpand}
            className={cn(
              isDarkMode 
                ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]"
                : "text-gray-600 hover:text-gray-900",
            )}
          >
            Calendar {calendarExpanded ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
          </Button>
          
          {onBackToDashboard && (
            <Button
              variant="outline"
              size="icon"
              onClick={onBackToDashboard}
              className="bg-[#818881]/20 text-[#D9D9D9]"
            >
              <LayoutDashboard className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Mobile view selector */}
      {isMobile && calendarExpanded && (
        <div className="md:hidden flex mt-2 bg-[#2C2E2D] p-2 rounded-lg border border-[#818881]">
          <Select value={viewMode} onValueChange={(value: ViewMode) => setViewMode(value)}>
            <SelectTrigger className={cn(
              "w-full",
              isDarkMode 
                ? "bg-[#2C2E2D] border-[#818881] text-[#D9D9D9]"
                : "bg-white"
            )}>
              <SelectValue placeholder="View" />
            </SelectTrigger>
            <SelectContent className={cn(
              isDarkMode ? "bg-[#2C2E2D] border-[#818881]" : "bg-white"
            )}>
              <SelectItem value="month">Month View</SelectItem>
              <SelectItem value="week">Week View</SelectItem>
              <SelectItem value="day">Day View</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Calendar - different layouts for mobile and desktop */}
      {(showCalendar || calendarExpanded) && (
        <>
          {/* Only show weekday headers in week or month view */}
          {(viewMode === 'week' || viewMode === 'month') && (
            <div className={cn(
              "grid gap-1 md:gap-4",
              "grid-cols-7"
            )}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div 
                  key={day}
                  className={cn(
                    "text-center text-xs md:text-sm font-medium py-1 md:py-2",
                    isDarkMode ? "text-[#818881]" : "text-gray-500"
                  )}
                >
                  {isMobile ? day.substring(0, 1) : day}
                </div>
              ))}
            </div>
          )}
          
          {/* Calendar days grid */}
          <div className={cn(
            "grid gap-1 md:gap-4 animate-in fade-in slide-in-from-top-4",
            viewMode === 'month' ? "grid-cols-7" : 
            viewMode === 'week' ? "grid-cols-7" : 
            "grid-cols-5"  // Day view shows 5 days on mobile
          )}>
            {calendarDays.map((date) => (
              <DayCard
                key={date.toISOString()}
                date={date}
                isCurrentMonth={isSameMonth(date, currentDate)}
                isSelected={selectedDate ? isSameDay(date, selectedDate) : false}
                onClick={() => handleDayClick(date)}
                isDarkMode={isDarkMode}
                isMobile={isMobile}
              />
            ))}
          </div>
        </>
      )}

      {/* Day detail view */}
      {!isMobile || !calendarExpanded ? (
        <DayDetail
          date={selectedDate}
          isOpen={showDayDetail}
          onClose={() => {
            setShowDayDetail(false);
            setShowCalendar(true);
          }}
          isDarkMode={isDarkMode}
          demoMode={demoMode}
        />
      ) : null}
    </div>
  );
};

export default FocusMode; 